import React, { useEffect, useRef, useState, useCallback } from "react";
import {
  HMSRoomProvider,
  useHMSActions,
  useHMSStore,
  useVideo,
  useHMSNotifications,
  HMSNotificationTypes,
  selectPeers,
  selectIsConnectedToRoom,
  selectHMSMessages,
  selectLocalPeer,
  selectLocalVideoTrackID,
  selectIsLocalAudioEnabled,
  selectIsLocalVideoEnabled,
  selectIsLocalScreenShared,
  selectPeerScreenSharing,
  selectScreenShareByPeerID,
  selectCameraStreamByPeerID,
  selectIsPeerVideoEnabled,
} from "@100mslive/react-sdk";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
  Mic,
  MicOff,
  Video,
  VideoOff,
  Hand,
  PhoneOff,
  Send,
  MessageSquare,
  Loader2,
  ScreenShare,
  X,
  Smile,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "react-toastify";
import axiosInstance from "../../api/axiosInstance";
import { initializeSocket, getSocket } from "../../utils/socket";

/* ─── Reactions ─────────────────────────────────────────────── */
const REACTIONS = ["👍", "❤️", "😂", "🎉", "👏", "🔥", "😮", "💯"];

/* Map raw getUserMedia/HMS errors to a message users can act on */
function friendlyMediaError(err, kind = "camera/mic") {
  const name = err?.name || "";
  if (name === "DeviceInUse") {
    return `Your ${kind} is being used by another app or browser tab. Close it and try again.`;
  }
  if (name === "NotFoundError" || name === "DevicesNotFoundError") {
    return `No ${kind} was found on this device.`;
  }
  if (name === "NotAllowedError" || name === "PermissionDeniedError") {
    return `Permission to use your ${kind} was denied. Allow access in your browser settings.`;
  }
  return err?.message || `Could not access your ${kind}.`;
}

/* ─── Floating Reaction ─────────────────────────────────────── */
const FloatingReaction = ({ emoji, id, onDone }) => {
  useEffect(() => {
    const t = setTimeout(onDone, 3000);
    return () => clearTimeout(t);
  }, [onDone]);

  const left = 10 + Math.random() * 80;

  return (
    <motion.div
      className="fixed bottom-28 z-50 text-4xl pointer-events-none select-none"
      style={{ left: `${left}%` }}
      initial={{ opacity: 1, y: 0 }}
      animate={{ opacity: 0, y: -220 }}
      transition={{ duration: 3, ease: "easeOut" }}
    >
      {emoji}
    </motion.div>
  );
};

/* ─── Control Button ─────────────────────────────────────────── */
const ControlBtn = ({ onClick, icon, label, danger = false, highlight = false }) => (
  <button onClick={onClick} type="button" className="flex flex-col items-center gap-1.5 group select-none">
    <div className={`w-12 h-12 rounded-full flex items-center justify-center transition-all duration-200 active:scale-95 shadow-md
      ${danger ? "bg-red-600 hover:bg-red-700 shadow-red-600/20" : highlight ? "bg-blue-600 hover:bg-blue-700 shadow-blue-600/20" : "bg-slate-800 hover:bg-slate-700"}
    `}>
      <span className="text-white">{icon}</span>
    </div>
    <span className="text-[11px] font-medium text-slate-400 group-hover:text-slate-200 truncate max-w-[70px]">{label}</span>
  </button>
);

/* ─── Video Tile ─────────────────────────────────────────────── */
function VideoTile({ peer }) {
  // Reactively track camera track and screen share track
  const localVideoTrackID = useHMSStore(selectLocalVideoTrackID);
  const videoTrack = useHMSStore(selectCameraStreamByPeerID(peer.id));
  const screenTrack = useHMSStore(selectScreenShareByPeerID(peer.id));
  const isVideoEnabled = useHMSStore(selectIsPeerVideoEnabled(peer.id));
  const isLocalVideoOn = useHMSStore(selectIsLocalVideoEnabled);

  const trackId = peer.isLocal ? localVideoTrackID : (videoTrack?.id || peer.videoTrack);
  const { videoRef } = useVideo({ trackId });
  const { videoRef: screenRef } = useVideo({ trackId: screenTrack?.id });

  let peerMeta = {};
  try { peerMeta = peer.metadata ? JSON.parse(peer.metadata) : {}; } catch {}
  const hasRaisedHand = !peer.isLocal && peerMeta.isHandRaised;

  const isScreenSharing = !!screenTrack;
  const showVideo = !isScreenSharing && (peer.isLocal ? isLocalVideoOn : isVideoEnabled);
  const initials = (peer.name || "User")
    .split(" ")
    .filter(Boolean)
    .map((p) => p[0])
    .join("")
    .slice(0, 2)
    .toUpperCase() || "U";

  return (
    <div className="relative w-full h-full bg-[#0b132b] rounded-3xl overflow-hidden border border-slate-800/80 shadow-2xl flex-1 min-h-0 flex items-center justify-center">
      {/* Screen share video */}
      <video
        ref={screenRef}
        autoPlay
        playsInline
        className={`w-full h-full object-contain ${isScreenSharing ? "block" : "hidden"}`}
      />

      {/* Camera video */}
      <video
        ref={videoRef}
        autoPlay
        muted={peer.isLocal}
        playsInline
        className={`w-full h-full object-cover ${showVideo ? "block" : "hidden"}`}
      />

      {/* Camera Off Avatar Placeholder */}
      {!isScreenSharing && !showVideo && (
        <div className="flex flex-col items-center justify-center p-6 text-center select-none animate-in fade-in duration-300 z-10">
          <div className="relative flex items-center justify-center mb-4">
            <div className="w-28 h-28 sm:w-36 sm:h-36 rounded-full bg-gradient-to-tr from-blue-600 via-indigo-600 to-sky-400 p-1 shadow-2xl shadow-blue-500/20">
              <div className="w-full h-full rounded-full bg-[#0d1b2a] flex items-center justify-center text-white font-black text-3xl sm:text-4xl tracking-wider">
                {initials}
              </div>
            </div>
            <div className="absolute -bottom-1 -right-1 p-2 bg-slate-800/90 border border-slate-700 text-slate-300 rounded-full shadow-lg">
              <VideoOff className="w-4 h-4 text-rose-400" />
            </div>
          </div>
          <p className="text-slate-200 font-bold text-base sm:text-lg tracking-tight">
            {peer.name} {peer.isLocal && "(You)"}
          </p>
          <span className="text-xs text-slate-400 mt-1 flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-slate-500" /> Camera is turned off
          </span>
        </div>
      )}

      {/* Floating Badges */}
      <div className="absolute bottom-4 left-4 flex items-center gap-2 z-20">
        <span className="bg-black/60 backdrop-blur-md text-white text-xs font-semibold px-3 py-1.5 rounded-full border border-white/10 shadow-md">
          {peer.name} {peer.isLocal && "(You)"}
        </span>
        {hasRaisedHand && (
          <motion.span
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="text-base bg-amber-400/20 border border-amber-400/30 px-2 py-0.5 rounded-full shadow-sm text-amber-300"
          >
            ✋ Hand Raised
          </motion.span>
        )}
      </div>
    </div>
  );
}

/* ─── Main User Room ──────────────────────────────────────────── */
function UserRoom() {
  const { bookingId } = useParams();
  const [searchParams] = useSearchParams();
  const urlToken = searchParams.get("token");
  const navigate = useNavigate();
  const hmsActions = useHMSActions();
  const notification = useHMSNotifications();
// eslint-disable-next-line
  const peers = useHMSStore(selectPeers);// eslint-disable-next-line
  const localPeer = useHMSStore(selectLocalPeer);
  const isConnected = useHMSStore(selectIsConnectedToRoom);
  const messages = useHMSStore(selectHMSMessages);

  const isAudioOn = useHMSStore(selectIsLocalAudioEnabled);
  const isVideoOn = useHMSStore(selectIsLocalVideoEnabled);
  const isScreenShared = useHMSStore(selectIsLocalScreenShared);

  const [chat, setChat] = useState("");
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [lastReadCount, setLastReadCount] = useState(0);
  const [showReactions, setShowReactions] = useState(false);
  const [floatingReactions, setFloatingReactions] = useState([]);
  const [isJoining, setIsJoining] = useState(false);
  const hasJoinedRef = useRef(false);
  const isJoiningRef = useRef(false);
  const chatEndRef = useRef(null);
  const prevMsgLen = useRef(0);

  /* Log SDK notifications for debugging without showing false alarm banners */
  useEffect(() => {
    if (!notification) return;
    if (notification.type === HMSNotificationTypes.ERROR) {
      console.warn("HMS Notification Error:", notification.data);
    }
  }, [notification]);

  /* Auto-scroll */
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    if (isChatOpen) setLastReadCount(chatMessages.length);// eslint-disable-next-line
  }, [isChatOpen, messages]);

  const startCall = useCallback(async () => {
    if (hasJoinedRef.current || isJoiningRef.current) return;
    try {
      isJoiningRef.current = true;
      setIsJoining(true);

      // Always validate/fetch through backend to verify booking is not completed
      const res = await axiosInstance.get(`/users/bookings/${bookingId}/video-token`);
      const token = res.data?.token || urlToken;

      if (!token) {
        throw new Error("Unable to obtain call token");
      }

      const storedName = localStorage.getItem("userName") || sessionStorage.getItem("userName");
      const displayName = storedName ? storedName.trim() : "Client";

      // Join with camera and mic directly enabled (standard 100ms flow)
      try {
        await hmsActions.join({
          authToken: token,
          userName: displayName,
          rememberDeviceSelection: false,
          settings: { isAudioMuted: false, isVideoMuted: false },
        });
      } catch (mediaErr) {
        console.warn("Direct AV join failed, falling back to muted join:", mediaErr);
        await hmsActions.join({
          authToken: token,
          userName: displayName,
          rememberDeviceSelection: false,
          settings: { isAudioMuted: true, isVideoMuted: true },
        });
        toast.warning(friendlyMediaError(mediaErr));
      }

      hasJoinedRef.current = true;
    } catch (err) {
      console.error("Join error:", err);
      alert(err.response?.data?.message || err.message || "Failed to connect to video room.");
      navigate("/profile/my-bookings");
    } finally {
      setIsJoining(false);
      isJoiningRef.current = false;
    }
  }, [bookingId, urlToken, hmsActions, navigate]);

  /* Join once on mount - STABLE REF PREVENTS RERENDER CLEANUP */
  const startCallRef = useRef(startCall);
  startCallRef.current = startCall;

  useEffect(() => {
    startCallRef.current();

    // Listen for expert completing booking in real-time
    const userAuthToken = localStorage.getItem("token") || sessionStorage.getItem("token");
    const socket = initializeSocket(userAuthToken) || getSocket();
    if (socket) {
      const handleStatusUpdate = (data) => {
        const isMatch =
          data?.bookingId === bookingId ||
          data?.bookingCode === bookingId ||
          data?.bookingMongoId === bookingId;
        if (isMatch && data?.status?.toLowerCase() === "completed") {
          alert("The expert has marked this consultation as completed.");
          hmsActions.leave().catch(() => {});
          navigate("/profile/my-bookings");
        }
      };
      socket.on("booking:status_updated", handleStatusUpdate);
      return () => {
        socket.off("booking:status_updated", handleStatusUpdate);
      };
    }

    const handleBeforeUnload = () => {
      hmsActions.leave().catch(() => {});
    };
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      hmsActions.leave().catch(() => {});
    }; // eslint-disable-next-line 
  }, [bookingId, hmsActions, navigate]);

  /* Incoming reactions from remote peers */
  useEffect(() => {
    const newMsgs = messages.slice(prevMsgLen.current);
    newMsgs.forEach((m) => {
      if (m.message.startsWith("REACTION:") && m.senderName !== localPeer?.name) {
        const emoji = m.message.replace("REACTION:", "");
        const id = Date.now() + Math.random();
        setFloatingReactions((prev) => [...prev, { emoji, id }]);
      }
    });
    prevMsgLen.current = messages.length;// eslint-disable-next-line
  }, [messages]);

  const removeFloating = useCallback((id) => {
    setFloatingReactions((prev) => prev.filter((r) => r.id !== id));
  }, []);

  /* Handlers with error handling */
  const toggleAudio = async () => {
    try {
      await hmsActions.setLocalAudioEnabled(!isAudioOn);
    } catch (e) {
      console.error("Audio toggle failed:", e);
      toast.error(friendlyMediaError(e, "microphone"));
    }
  };

  const toggleVideo = async () => {
    try {
      await hmsActions.setLocalVideoEnabled(!isVideoOn);
    } catch (e) {
      console.error("Video toggle failed:", e);
      toast.error(friendlyMediaError(e, "camera"));
    }
  };

  const toggleScreenShare = async () => {
    try {
      await hmsActions.setScreenShareEnabled(!isScreenShared);
    } catch (error) {
      console.error("Screen share error:", error);
      alert(error.message || "Screen share failed");
    }
  };

  const leaveCall = async () => {
    try {
      await hmsActions.leave();
    } catch (_) {}
    try {
      await axiosInstance.post(`/users/bookings/${bookingId}/video-leave`);
    } catch (_) {}
    navigate("/profile/my-bookings");
  };

  const peerSharing = useHMSStore(selectPeerScreenSharing);
  const anyoneSharing = peerSharing || isScreenShared;

  const sendMessage = () => {
    if (!chat.trim()) return;
    hmsActions.sendBroadcastMessage(chat);
    setChat("");
  };

  const sendReaction = async (emoji) => {
    const id = Date.now() + Math.random();
    setFloatingReactions((prev) => [...prev, { emoji, id }]);
    await hmsActions.sendBroadcastMessage(`REACTION:${emoji}`);
    setShowReactions(false);
  };

  const isHandRaised = (() => {
    try { return localPeer?.metadata ? JSON.parse(localPeer.metadata).isHandRaised : false; } catch { return false; }
  })();

  const toggleRaiseHand = async () => {
    await hmsActions.changeMetadata(JSON.stringify({ isHandRaised: !isHandRaised }));
  };

  /* Derived */
  const uniquePeers = peers.filter(
    (peer, index, self) => index === self.findIndex((p) => p.id === peer.id)
  );

  const chatMessages = messages.filter((m) => !m.message.startsWith("REACTION:"));
  const unread = chatMessages.length - lastReadCount;

  /* Loading / Pre-join state */
  if (!isConnected) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-slate-950 z-[999] p-4 font-outfit">
        <div className="text-center p-8 sm:p-10 bg-slate-900/90 border border-slate-800 rounded-3xl max-w-md w-full shadow-2xl backdrop-blur-xl">
          <div className="w-20 h-20 bg-blue-600/20 border border-blue-500/30 rounded-2xl mx-auto flex items-center justify-center mb-6 shadow-lg shadow-blue-500/10">
            {isJoining ? (
              <Loader2 className="w-10 h-10 text-blue-400 animate-spin" />
            ) : (
              <Video className="w-10 h-10 text-blue-400" />
            )}
          </div>
          <h2 className="text-2xl font-black text-white mb-2 tracking-tight">Live Consultation</h2>
          <p className="text-slate-400 mb-8 text-sm leading-relaxed">
            {isJoining ? "Connecting to secure video room..." : "Ready to join your consultation session."}
          </p>

          <button
            type="button"
            onClick={startCall}
            disabled={isJoining}
            className="w-full py-4 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 active:scale-[0.98] text-white font-bold text-sm uppercase tracking-wider rounded-2xl transition-all shadow-xl shadow-blue-600/25 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {isJoining ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Connecting...</span>
              </>
            ) : (
              <>
                <Video className="w-4 h-4" />
                <span>Join Call Now</span>
              </>
            )}
          </button>

          <button
            type="button"
            onClick={() => navigate("/profile/my-bookings")}
            className="mt-4 text-xs font-semibold text-slate-500 hover:text-slate-300 transition-colors block w-full text-center"
          >
            Cancel and return to bookings
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[100] bg-slate-950 flex flex-col overflow-hidden font-outfit">

      {/* ── HEADER (STANDALONE CLEAN TOPBAR) ──────────────────── */}
      <div className="flex-shrink-0 flex items-center justify-between px-6 py-3.5 bg-slate-900/95 backdrop-blur-md border-b border-slate-800/80 z-30">
        <div className="flex items-center gap-3">
          <span className="font-extrabold text-blue-400 text-base tracking-tight">EasyMarg</span>
          <span className="text-slate-600">|</span>
          <div className="flex items-center gap-2 bg-blue-500/10 border border-blue-500/20 px-3 py-1 rounded-full">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            <span className="text-xs font-bold text-blue-300 uppercase tracking-wider">Live Consultation</span>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {isHandRaised && (
            <motion.span
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="text-amber-300 text-xs font-bold bg-amber-400/10 px-3 py-1 rounded-full border border-amber-400/20 flex items-center gap-1.5"
            >
              ✋ Hand Raised
            </motion.span>
          )}
          <span className="text-xs font-semibold text-slate-300 bg-slate-800/90 px-3 py-1.5 rounded-full border border-slate-700/80">
            👥 {uniquePeers.length} Participant{uniquePeers.length !== 1 ? "s" : ""}
          </span>
        </div>
      </div>



      {/* ── VIDEO GRID ─────────────────────────────────────────── */}
      <div className="flex-1 overflow-hidden p-4 sm:p-6">
        <div className={`grid gap-4 sm:gap-6 h-full ${
          anyoneSharing ? "grid-cols-1" :
          uniquePeers.length <= 1 ? "grid-cols-1" :
          uniquePeers.length <= 2 ? "grid-cols-1 md:grid-cols-2" : "grid-cols-2"
        }`}>
          {uniquePeers.map((peer) => (
            <VideoTile key={peer.id} peer={peer} />
          ))}
        </div>
      </div>

      {/* ── FLOATING REACTIONS ──────────────────────────────────── */}
      {floatingReactions.map(({ emoji, id }) => (
        <FloatingReaction key={id} emoji={emoji} id={id} onDone={() => removeFloating(id)} />
      ))}

      {/* ── CHAT PANEL ─────────────────────────────────────────── */}
      <AnimatePresence>
        {isChatOpen && (
          <motion.div
            initial={{ opacity: 0, x: 360 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 360 }}
            transition={{ type: "spring", damping: 25 }}
            className="fixed top-14 right-0 bottom-24 w-80 sm:w-96 z-40 flex flex-col bg-slate-900/98 backdrop-blur-xl border-l border-slate-800 shadow-2xl"
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-800">
              <div className="flex items-center gap-2">
                <MessageSquare className="w-4 h-4 text-blue-400" />
                <h3 className="text-white font-bold text-sm">Session Chat</h3>
              </div>
              <button onClick={() => setIsChatOpen(false)} className="p-1 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
              {chatMessages.length === 0 && (
                <div className="text-center py-12">
                  <p className="text-slate-500 text-xs">No messages yet in this session</p>
                  <p className="text-slate-600 text-[11px] mt-1">Send a message to everyone in the room</p>
                </div>
              )}
              {chatMessages.map((m) => (
                <div key={m.id} className={`flex flex-col ${m.senderName === localPeer?.name ? "items-end" : "items-start"}`}>
                  <span className="text-[10px] text-slate-500 mb-0.5">{m.senderName}</span>
                  <div className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed ${m.senderName === localPeer?.name ? "bg-blue-600 text-white rounded-tr-sm" : "bg-slate-800 text-slate-200 rounded-tl-sm border border-slate-700/50"}`}>
                    {m.message}
                  </div>
                </div>
              ))}
              <div ref={chatEndRef} />
            </div>
            <div className="p-4 border-t border-slate-800 bg-slate-900/50">
              <div className="flex gap-2">
                <input
                  value={chat}
                  onChange={(e) => setChat(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && sendMessage()}
                  placeholder="Type a message..."
                  className="flex-1 bg-slate-800 border border-slate-700 rounded-xl px-4 py-2.5 text-white text-sm outline-none placeholder-slate-500 focus:border-blue-500 transition-colors"
                />
                <button onClick={sendMessage} className="bg-blue-600 hover:bg-blue-700 w-11 rounded-xl flex items-center justify-center transition-colors shadow-md shadow-blue-600/20">
                  <Send className="w-4 h-4 text-white" />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── CONTROLS BAR ───────────────────────────────────────── */}
      <div className="flex-shrink-0 bg-slate-900/95 backdrop-blur-md border-t border-slate-800/80 h-24 flex items-center justify-center gap-3 sm:gap-4 px-6 z-30">

        {/* Mic */}
        <ControlBtn
          onClick={toggleAudio}
          icon={isAudioOn ? <Mic className="w-5 h-5" /> : <MicOff className="w-5 h-5" />}
          label={isAudioOn ? "Mute" : "Unmute"}
          danger={!isAudioOn}
        />

        {/* Camera */}
        <ControlBtn
          onClick={toggleVideo}
          icon={isVideoOn ? <Video className="w-5 h-5" /> : <VideoOff className="w-5 h-5" />}
          label={isVideoOn ? "Stop Video" : "Start Video"}
          danger={!isVideoOn}
        />

        {/* Screen Share */}
        <ControlBtn
          onClick={toggleScreenShare}
          icon={<ScreenShare className="w-5 h-5" />}
          label={isScreenShared ? "Stop Share" : "Share"}
          highlight={isScreenShared}
        />

        {/* Raise Hand */}
        <ControlBtn
          onClick={toggleRaiseHand}
          icon={<Hand className="w-5 h-5" />}
          label={isHandRaised ? "Lower Hand" : "Raise Hand"}
          highlight={isHandRaised}
        />

        {/* Reactions (FIXED HORIZONTAL ALIGNMENT) */}
        <div className="relative">
          <ControlBtn
            onClick={() => setShowReactions((v) => !v)}
            icon={<Smile className="w-5 h-5" />}
            label="React"
            highlight={showReactions}
          />
          <AnimatePresence>
            {showReactions && (
              <motion.div
                initial={{ opacity: 0, y: 10, scale: 0.9 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 10, scale: 0.9 }}
                className="absolute bottom-20 left-1/2 -translate-x-1/2 bg-slate-900/95 backdrop-blur-xl border border-slate-700/80 rounded-full px-3 py-2 shadow-2xl z-50 flex items-center gap-1.5 w-max min-w-max"
              >
                {REACTIONS.map((emoji) => (
                  <button
                    key={emoji}
                    type="button"
                    onClick={() => sendReaction(emoji)}
                    className="w-10 h-10 flex items-center justify-center text-2xl hover:scale-130 hover:bg-white/10 rounded-full transition-all duration-150 active:scale-95 select-none"
                    title={`React ${emoji}`}
                  >
                    {emoji}
                  </button>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Chat */}
        <div className="relative">
          <ControlBtn
            onClick={() => { setIsChatOpen((v) => !v); if (!isChatOpen) setLastReadCount(chatMessages.length); }}
            icon={<MessageSquare className="w-5 h-5" />}
            label="Chat"
            highlight={isChatOpen}
          />
          {unread > 0 && !isChatOpen && (
            <span className="absolute -top-1 -right-1 bg-red-500 text-[10px] w-5 h-5 rounded-full flex items-center justify-center font-bold text-white shadow-md animate-pulse">
              {unread}
            </span>
          )}
        </div>

        {/* Leave Call */}
        <button
          type="button"
          onClick={leaveCall}
          className="flex flex-col items-center gap-1.5 group ml-2 sm:ml-4 select-none"
          title="Leave Call"
        >
          <div className="w-12 h-12 bg-rose-600 hover:bg-rose-700 rounded-full flex items-center justify-center transition-all active:scale-95 shadow-lg shadow-rose-600/30">
            <PhoneOff className="w-5 h-5 text-white" />
          </div>
          <span className="text-[11px] font-semibold text-slate-400 group-hover:text-rose-400">Leave</span>
        </button>
      </div>
    </div>
  );
}

export default function VideoCall() {
  return (
    <HMSRoomProvider>
      <UserRoom />
    </HMSRoomProvider>
  );
}